import React, { FC, Fragment, useEffect, useState } from 'react';
import { Box, Divider, Paper, styled, Tab, Theme } from '@mui/material';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { useParams, useNavigate } from 'react-router-dom';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { useQuery } from '@apollo/client';
import { getSupplierTemplate } from 'apollo/templates/client-directory';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { useTranslation } from 'react-i18next';
import { permissions } from 'utils/permission';
import SupplierDetails from 'components/suppliers/supplier-settings/supplier-details/supplier-details.component';
import SupplierSettingsHeader from 'components/suppliers/supplier-settings/supplier-settings-header/supplier-settings-header.component';
import SubcontractDefaultsSettings from 'components/suppliers/supplier-settings/subcontract-defaults/subcontract-defaults.component';

const SupplierSettings: FC = () => {
    const { t: translate } = useTranslation(['common', 'client-directory', 'organization', 'orders']);
    const currentUser = currentUserVar();
    const { tab, id } = useParams<{ tab: string; id: string }>();
    const [tabState, setTabState] = useState('supplier-details');
    const navigate = useNavigate();
    const { showBoundary } = useDenthubErrorBoundary();

    const { data: { supplier } = {} } = useQuery(getSupplierTemplate, {
        notifyOnNetworkStatusChange: true,
        variables: {
            supplierId: id,
        },
        onError(error) {
            showBoundary(error);
        },
    });

    useEffect(() => {
        setTabState(tab || 'supplier-details');
    }, [tab]);

    const handleTabChange = (_: unknown, newTabState: string) => {
        const routeMap: { [key: string]: string } = {
            'supplier-details': 'supplier-details#contactDetails',
            // DD-3485: this lab's subcontract defaults for the supplier.
            subcontracting: 'subcontracting',
        };

        if (tabState !== newTabState) {
            const url = `/supplier-directory/${id}/${routeMap[newTabState]}`;
            navigate(url);
            setTabState(newTabState);
        }
    };

    return (
        <Fragment>
            {permissions.isLabTechnician(currentUser.type) ? (
                <Fragment>
                    {supplier ? (
                        <Paper variant="outlined" style={{ overflowX: 'auto' }}>
                            <SupplierSettingsHeader labClient={supplier} />
                            <TabContext value={tabState}>
                                <Box display="flex" mb={2} ml={2}>
                                    <Box display="flex">
                                        <TabList
                                            sx={{ minWidth: 250 }}
                                            onChange={handleTabChange}
                                            indicatorColor="primary"
                                            textColor="primary"
                                            orientation="vertical"
                                        >
                                            <SettingsTab
                                                label={translate('client-directory:SUPPLIER_DETAILS')}
                                                value="supplier-details"
                                            />
                                            <SettingsTab
                                                label={translate('orders:SUBCONTRACTING')}
                                                value="subcontracting"
                                            />
                                        </TabList>
                                        <Box mx={3}>
                                            <Divider orientation="vertical" />
                                        </Box>
                                    </Box>
                                    <Box flexGrow={1} pr={2}>
                                        <TabPanel sx={{ padding: 0 }} value="supplier-details">
                                            <SupplierDetails labClient={supplier} />
                                        </TabPanel>
                                        <TabPanel sx={{ padding: 0 }} value="subcontracting">
                                            <SubcontractDefaultsSettings labClient={supplier} />
                                        </TabPanel>
                                    </Box>
                                </Box>
                            </TabContext>
                        </Paper>
                    ) : undefined}
                </Fragment>
            ) : undefined}
        </Fragment>
    );
};

interface SettingsTabProps {
    label: string;
    value: string;
    theme?: Theme;
}
const SettingsTab = styled((props: SettingsTabProps) => <Tab {...props} />)(({ theme }: { theme?: Theme }) => {
    return {
        color: theme?.palette.primary.main,
        maxWidth: 'none',
        '&.Mui-selected': {
            fontWeight: theme?.typography.fontWeightBold,
            background: theme?.palette.primary.light,
            color: '#fff',
        },
        '&:hover': {
            opacity: 0.8,
        },
        alignItems: 'start',
        textAlign: 'left',
    };
});
export default SupplierSettings;
